import { Component, OnInit, DestroyRef, inject } from '@angular/core';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, switchMap, tap, catchError, EMPTY, distinctUntilChanged, debounceTime } from 'rxjs';
import { EmailsService } from '../../core/services/emails.service';
import {
  BLACKLIST_REASONS, DateRange, EmailCategory, EmailCounts, EmailFilters,
  EmailListItem, GreylistFilter, INTENT_GROUPS
} from '../../core/models/email.model';
import { TranslateModule } from '@ngx-translate/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { provideNativeDateAdapter } from '@angular/material/core';
import { RelativeDatePipe } from './relative-date.pipe';

const PAGE_SIZES = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;

@Component({
  selector: 'app-email-complaints',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, TranslateModule,
    MatFormFieldModule, MatSelectModule, MatDatepickerModule, MatInputModule, RelativeDatePipe],
  providers: [provideNativeDateAdapter()],
  templateUrl: './email-complaints.component.html',
  styleUrls: ['./email-complaints.component.css']
})
export class EmailComplaintsComponent implements OnInit {

  private destroyRef    = inject(DestroyRef);
  private loadTrigger$  = new Subject<void>();
  private countsTrigger$ = new Subject<void>();
  private searchTrigger$ = new Subject<string>();
  private dateTrigger$   = new Subject<void>();

  /** Serialized snapshots of the last params each pipeline loaded for, to skip redundant calls. */
  private lastListKey:   string | null = null;
  private lastCountsKey: string | null = null;

  // ── List state ────────────────────────────────────────────────────────────
  emails: EmailListItem[] = [];
  activeCard: EmailCategory | null = null;
  currentPage  = 0;
  totalElements = 0;
  pageSize     = DEFAULT_PAGE_SIZE;
  totalPages   = 0;
  sortDir: 'asc' | 'desc' = 'desc';
  isLoading    = false;
  errorMessage = '';
  counts: EmailCounts | null = null;
  countsError  = false;

  // ── Global filters ────────────────────────────────────────────────────────
  dateRange: DateRange | null = null;
  customFrom: Date | null = null;
  customTo:   Date | null = null;

  get hasDateFilter(): boolean {
    return this.dateRange !== null || !!this.customFrom || !!this.customTo;
  }

  // ── List-level filters ────────────────────────────────────────────────────
  searchText   = '';
  intentGroup: string | null = null;
  needsReview: boolean | null = null;
  greylistType: GreylistFilter = 'ALL';
  blacklistReason: string | null = null;

  // ── Filter options ────────────────────────────────────────────────────────
  readonly intentGroups = INTENT_GROUPS;
  readonly blacklistReasons = BLACKLIST_REASONS;
  readonly pageSizes = PAGE_SIZES;

  // ── Computed ──────────────────────────────────────────────────────────────

  get activeCardLabelKey(): string {
    switch (this.activeCard) {
      case 'all':          return 'EC_ALL_EMAILS';
      case 'white-listed': return 'EC_COMPLETED_EMAILS';
      case 'grey-listed':  return 'EC_PENDING_EMAILS';
      case 'black-listed': return 'EC_REJECTED_EMAILS';
      default:             return '';
    }
  }

  get showIntentFilter(): boolean {
    return this.activeCard === 'all' || this.activeCard === 'white-listed' || this.activeCard === 'grey-listed';
  }

  get showNeedsReviewFilter(): boolean {
    return this.activeCard === 'white-listed' || this.activeCard === 'grey-listed';
  }

  get showGreylistTypeFilter(): boolean {
    return this.activeCard === 'grey-listed';
  }

  get showBlacklistReasonFilter(): boolean {
    return this.activeCard === 'black-listed';
  }

  get hasActiveListFilters(): boolean {
    return !!this.searchText || !!this.intentGroup || this.needsReview != null
      || this.greylistType !== 'ALL' || !!this.blacklistReason;
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private emailsService: EmailsService
  ) { }

  ngOnInit() {
    // Counts pipeline — switchMap cancels stale in-flight requests
    this.countsTrigger$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.countsError = false),
        switchMap(() => {
          const { from, to } = this.dateRangeInstants();
          return this.emailsService.getCounts(from, to).pipe(
            catchError(() => {
              this.countsError = true;
              return EMPTY;
            })
          );
        })
      )
      .subscribe(counts => this.counts = counts);

    // Search debounce → URL
    this.searchTrigger$.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(text => this.navigate({ q: text.trim() || null, page: null }));

    // Manual date typing debounce → URL
    this.dateTrigger$.pipe(
      debounceTime(400),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => this.navigate({
      range: null,
      from: this.toDateParam(this.customFrom),
      to:   this.toDateParam(this.customTo),
      page: null
    }));

    // Main load pipeline
    this.loadTrigger$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => {
          this.isLoading    = true;
          this.errorMessage = '';
          this.emails       = [];
          this.totalElements = 0;
          this.totalPages   = 0;
        }),
        switchMap(() =>
          this.emailsService.getEmails(
            this.activeCard!,
            this.currentPage,
            this.pageSize,
            this.buildFilters(),
            'receivedAt',
            this.sortDir
          ).pipe(
            catchError(() => {
              this.isLoading    = false;
              this.errorMessage = 'EC_LOAD_ERROR';
              this.emails       = [];
              return EMPTY;
            })
          )
        )
      )
      .subscribe(response => {
        this.emails        = response.content;
        this.totalElements = response.totalElements;
        this.totalPages    = response.totalPages;
        this.isLoading     = false;
      });

    // URL is the single source of truth for tab, page, sort, and all filters
    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => this.applyParams(params));
  }

  // ── URL → state ───────────────────────────────────────────────────────────

  private applyParams(params: Params) {
    const tab = params['tab'] as EmailCategory | undefined;
    this.activeCard = tab === 'all' || tab === 'white-listed' || tab === 'grey-listed' || tab === 'black-listed'
      ? tab : null;

    this.currentPage = this.parsePage(params['page']);
    this.pageSize    = this.parseSize(params['size']);
    this.sortDir     = params['sort'] === 'asc' ? 'asc' : 'desc';

    // List-level filters
    this.searchText      = (params['q'] as string) || '';
    this.intentGroup     = (params['intent'] as string) || null;
    this.needsReview     = params['review'] === 'true' ? true : params['review'] === 'false' ? false : null;
    this.greylistType    = params['gtype'] === 'DOMAIN' || params['gtype'] === 'INFO' ? params['gtype'] : 'ALL';
    this.blacklistReason = (params['reason'] as string) || null;

    // Global date filter
    const range = params['range'] as DateRange | undefined;
    if (range === 'today' || range === 'week' || range === 'month') {
      this.dateRange = range;
      const { from, to } = this.computeQuickRange(range);
      this.customFrom = from;
      this.customTo   = to;
    } else {
      this.dateRange  = null;
      this.customFrom = this.parseDateParam(params['from']);
      this.customTo   = this.parseDateParam(params['to']);
    }

    // Reload counts only when the date window changed
    const countsKey = `${params['range'] ?? ''}|${params['from'] ?? ''}|${params['to'] ?? ''}`;
    if (countsKey !== this.lastCountsKey) {
      this.lastCountsKey = countsKey;
      this.countsTrigger$.next();
    }

    // Reload list only when something it depends on changed
    const listKey = ['tab', 'page', 'size', 'sort', 'q', 'intent', 'review', 'gtype', 'reason', 'range', 'from', 'to']
      .map(k => params[k] ?? '').join('|');
    if (listKey !== this.lastListKey) {
      this.lastListKey = listKey;
      if (this.activeCard) this.loadTrigger$.next();
    }
  }

  // ── Card selection ────────────────────────────────────────────────────────

  setActiveCard(card: EmailCategory) {
    // Tab change resets page and list filters; the global date filter is kept
    this.navigate({
      tab: card, page: null,
      q: null, intent: null, review: null, gtype: null, reason: null
    });
  }

  onCardKeydown(event: Event, card: EmailCategory) {
    event.preventDefault();
    this.setActiveCard(card);
  }

  // ── Date range (global) ───────────────────────────────────────────────────

  setDateRange(range: DateRange | null) {
    this.navigate({ range, from: null, to: null, page: null });
  }

  onCustomDateChange() {
    this.dateTrigger$.next();
  }

  clearDateRange() {
    this.navigate({ range: null, from: null, to: null, page: null });
  }

  // ── List-level filters ────────────────────────────────────────────────────

  onSearchInput() {
    this.searchTrigger$.next(this.searchText);
  }

  clearSearch() {
    this.searchText = '';
    this.searchTrigger$.next('');
    this.navigate({ q: null, page: null });
  }

  setIntentGroup(group: string | null) {
    this.navigate({ intent: group || null, page: null });
  }

  setNeedsReview(value: boolean | null) {
    this.navigate({ review: value === null ? null : String(value), page: null });
  }

  setGreylistType(type: GreylistFilter) {
    this.navigate({ gtype: type === 'ALL' ? null : type, page: null });
  }

  setBlacklistReason(reason: string | null) {
    this.navigate({ reason: reason || null, page: null });
  }

  clearListFilters() {
    this.searchText = '';
    this.searchTrigger$.next('');
    this.navigate({ q: null, intent: null, review: null, gtype: null, reason: null, page: null });
  }

  refresh() {
    this.countsTrigger$.next();
    if (this.activeCard) this.loadTrigger$.next();
  }

  retryCounts() {
    this.countsTrigger$.next();
  }

  // ── Pagination & sorting ──────────────────────────────────────────────────

  nextPage() {
    if (this.currentPage < this.totalPages - 1) {
      this.navigate({ page: this.currentPage + 1 });
    }
  }

  previousPage() {
    if (this.currentPage > 0) {
      this.navigate({ page: this.currentPage - 1 || null });
    }
  }

  setPageSize(size: number) {
    this.navigate({ size: size === DEFAULT_PAGE_SIZE ? null : size, page: null });
  }

  setSortDir(dir: 'asc' | 'desc') {
    this.navigate({ sort: dir === 'desc' ? null : dir, page: null });
  }

  // ── Actions ───────────────────────────────────────────────────────────────

  viewEmail(email: EmailListItem) {
    const effectiveType = this.getEffectiveType(email);
    if (effectiveType === 'black-listed') {
      // State is a fast path; the detail page falls back to the intake-thread API on refresh
      this.router.navigate(['/email-complaints', email.intakeId], {
        queryParams: { type: effectiveType },
        state: { email }
      });
    } else {
      // Rows without a draft (e.g. untrusted-domain greylisted) only have an intake id,
      // so tell the detail page which thread endpoint to use
      const id = email.draftId || email.intakeId;
      this.router.navigate(['/email-complaints', id], {
        queryParams: { type: effectiveType, by: email.draftId ? 'draft' : 'intake' }
      });
    }
  }

  viewComplaintData(email: EmailListItem) {
    this.router.navigate(['/email-complaints/review', email.intakeId], {
      queryParams: { type: this.getEffectiveType(email) }
    });
  }

  hasComplaintAction(email: EmailListItem): boolean {
    return !!email.draftId;
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  /** Merge the given params into the URL; null removes a param. `page: null` resets to page 0. */
  private navigate(queryParams: Params) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge'
    });
  }

  private parsePage(raw: unknown): number {
    const page = Number(raw);
    return Number.isFinite(page) && page > 0 ? Math.floor(page) : 0;
  }

  private parseSize(raw: unknown): number {
    const size = Number(raw);
    return PAGE_SIZES.includes(size) ? size : DEFAULT_PAGE_SIZE;
  }

  private computeQuickRange(range: DateRange): { from: Date; to: Date } {
    const now = new Date();
    let from: Date;
    if (range === 'today') {
      from = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    } else if (range === 'week') {
      const day = now.getDay() || 7;
      from = new Date(now.getFullYear(), now.getMonth(), now.getDate() - day + 1);
    } else {
      from = new Date(now.getFullYear(), now.getMonth(), 1);
    }
    return { from, to: now };
  }

  private parseDateParam(raw: unknown): Date | null {
    if (typeof raw !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
    const [y, m, d] = raw.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    return isNaN(date.getTime()) ? null : date;
  }

  private toDateParam(date: Date | null): string | null {
    if (!date || isNaN(date.getTime())) return null;
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }

  private buildFilters(): EmailFilters {
    const { from, to } = this.dateRangeInstants();
    return {
      from,
      to,
      search:       this.searchText || undefined,
      intentGroup:  this.intentGroup || undefined,
      needsReview:  this.needsReview ?? undefined,
      greylistType: this.greylistType !== 'ALL' ? this.greylistType : undefined,
      reason:       this.blacklistReason || undefined,
    };
  }

  private dateRangeInstants(): { from?: string; to?: string } {
    const from = this.customFrom ? this.customFrom.toISOString() : undefined;
    const to   = this.customTo
      ? new Date(this.customTo.getFullYear(), this.customTo.getMonth(), this.customTo.getDate(), 23, 59, 59).toISOString()
      : undefined;
    return { from, to };
  }

  private getEffectiveType(email: EmailListItem): Exclude<EmailCategory, 'all'> {
    if (this.activeCard && this.activeCard !== 'all') return this.activeCard;
    if (email.category) return email.category;
    // Fallback for responses without category: greylist marker wins over missing draft,
    // since NON_TRUSTED (grey-listed) intakes also have no draft
    if (email.greylistType) return 'grey-listed';
    if (!email.draftId) return 'black-listed';
    return 'white-listed';
  }

  trackByEmail(_: number, email: EmailListItem): number {
    return email.intakeId;
  }
}
